<template>
  <div class="block-preview-content">
    <!-- Заголовок блока -->
    <div class="block-header">
      <div class="block-type">{{ humanizeType(block.type) }}</div>
    </div>

    <!-- Предварительный просмотр содержимого -->
    <div class="block-content">
      <!-- Текстовые блоки -->
      <div v-if="isTextBlock" class="text-preview">
        <h3 v-if="block.payload?.title" class="text-lg font-semibold mb-2">
          {{ block.payload.title }}
        </h3>
        <p v-if="block.payload?.text" class="text-gray-700 dark:text-gray-300">
          {{ truncateText(block.payload.text, 150) }}
        </p>
        <div v-if="block.payload?.button" class="mt-3">
          <button class="inline-block px-4 py-2 bg-accent text-white rounded text-sm">
            {{ block.payload.button.text || 'Кнопка' }}
          </button>
        </div>
      </div>

      <!-- Hero блоки -->
      <div v-else-if="isHeroBlock" class="hero-preview">
        <div class="hero-background">
          <div v-if="block.payload?.image_url || block.payload?.image_file_id" class="hero-image">
            <img 
              :src="block.payload.image_url || getFileUrl(block.payload.image_file_id)" 
              :alt="block.payload.title || 'Hero изображение'"
              class="w-full h-32 object-cover rounded"
            />
          </div>
          <div v-else-if="block.payload?.video_url || block.payload?.video_file_id" class="hero-video">
            <video 
              :src="block.payload.video_url || getFileUrl(block.payload.video_file_id)" 
              class="w-full h-32 object-cover rounded"
              muted
            />
          </div>
          <div v-else class="hero-placeholder">
            <div class="w-full h-32 bg-gray-300 dark:bg-gray-700 rounded flex items-center justify-center">
              <span class="text-gray-600 dark:text-gray-400">Нет медиа</span>
            </div>
          </div>
        </div>
        <div class="hero-content mt-3">
          <h3 v-if="block.payload?.title" class="text-lg font-semibold mb-2">
            {{ block.payload.title }}
          </h3>
          <p v-if="block.payload?.text" class="text-gray-700 dark:text-gray-300 text-sm">
            {{ truncateText(block.payload.text, 100) }}
          </p>
          <div v-if="block.payload?.button" class="mt-2">
            <button class="inline-block px-3 py-1 bg-accent text-white rounded text-xs">
              {{ block.payload.button.text || 'Кнопка' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Медиа блоки -->
      <div v-else-if="isMediaBlock" class="media-preview">
        <div class="media-layout">
          <div v-if="block.payload?.image_url || block.payload?.image_file_id" class="media-image">
            <img 
              :src="block.payload.image_url || getFileUrl(block.payload.image_file_id)" 
              :alt="block.payload.title || 'Медиа'"
              class="w-24 h-24 object-cover rounded"
            />
          </div>
          <div v-else-if="block.payload?.video_url || block.payload?.video_file_id" class="media-video">
            <video 
              :src="block.payload.video_url || getFileUrl(block.payload.video_file_id)" 
              class="w-24 h-24 object-cover rounded"
              muted
            />
          </div>
          <div class="media-content ml-3 flex-1">
            <h4 v-if="block.payload?.title" class="font-semibold mb-1">
              {{ block.payload.title }}
            </h4>
            <p v-if="block.payload?.text" class="text-gray-700 dark:text-gray-300 text-sm">
              {{ truncateText(block.payload.text, 80) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Галереи -->
      <div v-else-if="isGalleryBlock" class="gallery-preview">
        <h4 v-if="block.payload?.title" class="font-semibold mb-2">
          {{ block.payload.title }}
        </h4>
        <div class="gallery-items">
          <div v-if="block.payload?.images?.length" class="images-grid">
            <div 
              v-for="(image, idx) in block.payload.images.slice(0, 4)" 
              :key="idx"
              class="gallery-item"
            >
              <img 
                :src="getGalleryImageUrl(image)" 
                :alt="image.title || `Изображение ${idx + 1}`"
                class="w-full h-16 object-cover rounded"
              />
            </div>
            <div v-if="block.payload.images.length > 4" class="gallery-more">
              <span class="text-xs text-gray-600 dark:text-gray-400">+{{ block.payload.images.length - 4 }}</span>
            </div>
          </div>
          <div v-else-if="block.payload?.videos?.length" class="videos-grid">
            <div 
              v-for="(video, idx) in block.payload.videos.slice(0, 4)" 
              :key="idx"
              class="gallery-item"
            >
              <video 
                :src="getGalleryVideoUrl(video)" 
                class="w-full h-16 object-cover rounded"
                muted
              />
            </div>
            <div v-if="block.payload.videos.length > 4" class="gallery-more">
              <span class="text-xs text-gray-600 dark:text-gray-400">+{{ block.payload.videos.length - 4 }}</span>
            </div>
          </div>
          <div v-else class="gallery-empty">
            <span class="text-gray-600 dark:text-gray-400 text-sm">Нет элементов</span>
          </div>
        </div>
      </div>

      <!-- Интерактивные блоки -->
      <div v-else-if="isInteractiveBlock" class="interactive-preview">
        <h4 v-if="block.payload?.title" class="font-semibold mb-2">
          {{ block.payload.title }}
        </h4>
        <div v-if="block.payload?.items?.length" class="items-list">
          <div 
            v-for="(item, idx) in block.payload.items.slice(0, 3)" 
            :key="idx"
            class="item-preview"
          >
            <div v-if="item.question" class="faq-item">
              <div class="question text-sm font-medium text-gray-900 dark:text-primary">{{ item.question }}</div>
              <div class="answer text-xs text-gray-600 dark:text-gray-400">{{ truncateText(item.answer, 50) }}</div>
            </div>
            <div v-else-if="item.title" class="icon-item">
              <div class="item-title text-sm text-gray-900 dark:text-primary">{{ item.title }}</div>
              <div class="item-desc text-xs text-gray-600 dark:text-gray-400">{{ truncateText(item.description, 40) }}</div>
            </div>
            <div v-else-if="item.text" class="tip-item">
              <div class="tip-text text-sm text-gray-900 dark:text-primary">{{ truncateText(item.text, 60) }}</div>
            </div>
          </div>
          <div v-if="block.payload.items.length > 3" class="items-more">
            <span class="text-xs text-gray-600 dark:text-gray-400">+{{ block.payload.items.length - 3 }} элементов</span>
          </div>
        </div>
        <div v-else class="items-empty">
          <span class="text-gray-600 dark:text-gray-400 text-sm">Нет элементов</span>
        </div>
      </div>

      <!-- Отдельные медиа -->
      <div v-else-if="isSingleMediaBlock" class="single-media-preview">
        <div v-if="block.payload?.image?.image_url || block.payload?.image?.file_id" class="single-image">
          <img 
            :src="block.payload.image.image_url || getFileUrl(block.payload.image.file_id)" 
            :alt="block.payload.title || 'Изображение'"
            class="w-full h-24 object-cover rounded"
          />
        </div>
        <div v-else-if="block.payload?.video_url || block.payload?.video_file_id" class="single-video">
          <video 
            :src="block.payload.video_url || getFileUrl(block.payload.video_file_id)" 
            class="w-full h-24 object-cover rounded"
            muted
          />
        </div>
        <div class="single-content mt-2">
          <h4 v-if="block.payload?.title" class="font-semibold text-sm">
            {{ block.payload.title }}
          </h4>
          <p v-if="block.payload?.text" class="text-gray-700 dark:text-gray-300 text-xs">
            {{ truncateText(block.payload.text, 60) }}
          </p>
        </div>
      </div>

      <!-- Fallback для неизвестных блоков -->
      <div v-else class="unknown-block">
        <div class="text-gray-600 dark:text-gray-400 text-sm">Неизвестный тип блока</div>
        <div class="text-xs text-gray-500 dark:text-gray-500 mt-1">{{ block.type }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BlockPreview',
  props: {
    block: { type: Object, required: true },
    typeOptions: { type: Array, default: () => [] }
  },
  computed: {
    isTextBlock() {
      return this.block.type?.includes('text-block');
    },
    isHeroBlock() {
      return this.block.type?.includes('hero');
    },
    isMediaBlock() {
      return this.block.type?.includes('image') || 
             this.block.type?.includes('video') ||
             this.block.type?.includes('text-left') ||
             this.block.type?.includes('text-right') ||
             this.block.type?.includes('square') ||
             this.block.type?.includes('rect');
    },
    isGalleryBlock() {
      return this.block.type?.includes('gallery');
    },
    isInteractiveBlock() {
      return this.block.type?.includes('icons') ||
             this.block.type?.includes('faq') ||
             this.block.type?.includes('list-tips');
    },
    isSingleMediaBlock() {
      return this.block.type === 'image' || this.block.type === 'video';
    }
  },
  methods: {
    humanizeType(type) {
      const t = this.typeOptions.find((t) => t.value === type);
      return t ? t.label : type;
    },
    
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    
    getFileUrl(fileId) {
      // Преобразуем file_id в URL для отображения в превью
      if (!fileId) return '';
      const API_URL = (process.env.VUE_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');
      return `${API_URL}/files/${fileId}`;
    },
    
    getGalleryImageUrl(image) {
      // Приоритет: image_url > image_file_id
      if (image.image_url) return image.image_url;
      if (image.image_file_id) return this.getFileUrl(image.image_file_id);
      if (image.file_id) return this.getFileUrl(image.file_id);
      return '';
    },
    
    getGalleryVideoUrl(video) {
      // Приоритет: video_url > video_file_id
      if (video.video_url) return video.video_url;
      if (video.video_file_id) return this.getFileUrl(video.video_file_id);
      if (video.file_id) return this.getFileUrl(video.file_id);
      return '';
    },
    
    getDisplayUrl(item) {
      // Получаем URL для отображения - либо уже готовый URL, либо конвертируем file_id
      if (item.image_url) return item.image_url;
      if (item.video_url) return item.video_url;
      if (item.image_file_id) return this.getFileUrl(item.image_file_id);
      if (item.video_file_id) return this.getFileUrl(item.video_file_id);
      if (item.file_id) return this.getFileUrl(item.file_id);
      return '';
    }
  }
}
</script>

<style scoped>
.block-preview-content {
  @apply space-y-3;
}

.block-header {
  @apply flex items-center justify-between;
}

.block-type {
  @apply text-sm font-medium text-gray-700 dark:text-gray-300;
  @apply bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded;
}

.block-content {
  @apply space-y-2;
}

.text-preview {
  @apply space-y-2;
}

.hero-preview {
  @apply space-y-2;
}

.hero-background {
  @apply relative;
}

.hero-placeholder {
  @apply bg-gray-300 dark:bg-gray-700 rounded;
}

.media-preview {
  @apply space-y-2;
}

.media-layout {
  @apply flex items-start;
}

.gallery-preview {
  @apply space-y-2;
}

.gallery-items {
  @apply space-y-2;
}

.images-grid,
.videos-grid {
  @apply grid grid-cols-4 gap-2;
}

.gallery-item {
  @apply relative;
}

.gallery-more {
  @apply flex items-center justify-center;
  @apply bg-gray-300 dark:bg-gray-700 rounded;
}

.interactive-preview {
  @apply space-y-2;
}

.items-list {
  @apply space-y-2;
}

.item-preview {
  @apply p-2 bg-gray-200 dark:bg-gray-800 rounded;
}

.faq-item {
  @apply space-y-1;
}

.icon-item {
  @apply space-y-1;
}

.tip-item {
  @apply space-y-1;
}

.items-more {
  @apply text-center pt-2;
}

.single-media-preview {
  @apply space-y-2;
}

.single-image,
.single-video {
  @apply relative;
}

.single-content {
  @apply space-y-1;
}

.unknown-block {
  @apply p-3 bg-gray-200 dark:bg-gray-800 rounded text-center;
}

/* Адаптивность */
@media (max-width: 768px) {
  .media-layout {
    @apply flex-col;
  }
  
  .media-content {
    @apply ml-0 mt-2;
  }
  
  .images-grid,
  .videos-grid {
    @apply grid-cols-2;
  }
}
</style>
